import { useMemo, useRef } from 'react';

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  ListItem,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import dayjs from 'dayjs';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';

import { ContactAvatar } from 'components/ContactAvatar';
import ImageViewer from 'components/ImageViewer';
import InfiniteScroll from 'components/InfiniteScroll';
import TagsV2 from 'components/TagsV2/TagsV2';
import useCurrentUser from 'hooks/useCurrentUser';
import DetailItem from 'pages/Contatos/components/DetailItems';
import { useAppDispatch, useAppSelector } from 'store';
import {
  AtendimentoInterfaceV2,
  atendimentosSelector,
} from 'store/reducers/atendimentos';
import {
  comentariosSelector,
  fetchComentarios,
  removerComentario,
  resetComentarios,
} from 'store/reducers/comentarios';
import getUsuarioNomeExibicao from 'utils/getUsuarioNomeExibicao';
import { normalizeDuration } from 'utils/normalizeDuration';

const useStyles = makeStyles(({ spacing, palette }) => ({
  card: {
    width: '100%',
    overflow: 'hidden',
    backgroundColor: palette.background.tertiary,
    '& > .MuiCardHeader-root': {
      paddingBottom: 0,
    },
    '& > .MuiCardContent-root': {
      padding: spacing(1, 2),
      paddingBottom: spacing(2),
      wordBreak: 'break-word',
    },
  },

  tagsWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    marginTop: 4,
  },
}));

export default function TabPanelAtendimento() {
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useCurrentUser();
  const inputRef = useRef<HTMLInputElement>(null);
  const classes = useStyles();

  const dispatch = useAppDispatch();

  const { historicoAtendimentoSelecionado } =
    useAppSelector(atendimentosSelector);

  const atendimento = useMemo(
    () => historicoAtendimentoSelecionado as AtendimentoInterfaceV2,
    [historicoAtendimentoSelecionado],
  );

  const {
    comentarios,
    comentariosCursor,
    loadingComentarios,
    loadingRemoverComentario,
  } = useAppSelector(comentariosSelector);

  const onfocus = () => {
    if (inputRef.current) inputRef.current.focus();
  };

  const onLoadNextComentarios = () => {
    const { afterCursor } = comentariosCursor;
    if (atendimento?.id && afterCursor) {
      dispatch(
        fetchComentarios({
          atendimentoId: atendimento?.id,
          cursor: { afterCursor },
        }),
      );
    }
  };

  const handleConfirmRemove = (comentarioId: string) => {
    confirm({
      title: <Typography variant="body2">Remover comentário</Typography>,
      description: (
        <Typography variant="caption">
          Deseja mesmo remover este comentário permanentemente?
        </Typography>
      ),
      cancellationText: 'Cancelar',
      confirmationText: 'Remover',
      confirmationButtonProps: { variant: 'contained', color: 'secondary' },
      dialogProps: { maxWidth: 'xs' },
    })
      .then(async () => {
        const atendimentoId = atendimento?.id;
        if (atendimentoId) {
          /**
           * Remove um comentario
           * Limpa e atualiza a lista de comentarios após a remoção.
           */
          await dispatch(removerComentario({ atendimentoId, comentarioId }))
            .unwrap()
            .then(() => {
              onfocus();

              dispatch(resetComentarios());
              dispatch(fetchComentarios({ atendimentoId }));
            })
            .catch(error => {
              if (error.response) enqueueSnackbar(error.response.data.message);
            });
        }
      })
      .catch(() => {
        //
      });
  };

  const renderComentarios = comentarios.map(comentario => {
    const { id, usuario, criadoEm, texto } = comentario;
    const fromMe = currentUser?.id === usuario.id;
    const autor = fromMe ? 'Você' : getUsuarioNomeExibicao(usuario);

    return (
      <ListItem key={id} disableGutters>
        <Card className={classes.card} elevation={0}>
          <CardHeader
            title={autor}
            titleTypographyProps={{ noWrap: true, variant: 'caption' }}
            subheader={dayjs(criadoEm).fromNow()}
            subheaderTypographyProps={{ variant: 'caption' }}
            avatar={
              <ImageViewer src={usuario.foto}>
                <ContactAvatar src={usuario.foto} />
              </ImageViewer>
            }
            action={
              fromMe && (
                <IconButton
                  aria-label="remove"
                  onClick={() => handleConfirmRemove(id)}
                  disabled={loadingRemoverComentario}
                >
                  {loadingRemoverComentario ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : (
                    <Close fontSize="small" />
                  )}
                </IconButton>
              )
            }
          />

          <CardContent>
            <Typography variant="caption" color="textSecondary" component="p">
              {texto}
            </Typography>
          </CardContent>
        </Card>
      </ListItem>
    );
  });

  return (
    <Box>
      <Box
        width="100%"
        display="flex"
        flexDirection="column"
        justifyContent="center"
      >
        <DetailItem
          marginBottom={8}
          label="Código"
          value={`${atendimento.code}`}
        />
        <DetailItem
          marginBottom={8}
          label="Iniciado em"
          value={dayjs(atendimento.startedIn).format('DD/MM/YY[, às] HH:mm')}
        />
        {atendimento.finalizadoPor && (
          <DetailItem
            marginBottom={8}
            label="Finalizado em"
            value={dayjs(atendimento.finishedIn).format('DD/MM/YY[, às] HH:mm')}
          />
        )}

        {atendimento.durationInSeconds && (
          <DetailItem
            marginBottom={8}
            label="Duração"
            value={normalizeDuration(atendimento.durationInSeconds)}
          />
        )}

        {!atendimento.finishedIn && (
          <DetailItem
            marginBottom={8}
            label="Atendente"
            value={atendimento.attendantCurrent?.name}
          />
        )}

        {atendimento.finishedIn && (
          <DetailItem
            marginBottom={8}
            label="Finalizado por"
            value={atendimento.attendantFinisher?.name}
          />
        )}

        {atendimento?.atendimentoReason && (
          <DetailItem
            marginBottom={8}
            label="Motivo do atendimento"
            value={
              <>
                <Typography variant="body2">
                  {atendimento.atendimentoReason.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {atendimento.atendimentoReason.description}
                </Typography>
              </>
            }
          />
        )}

        <DetailItem
          marginBottom={8}
          label="Equipe"
          value={atendimento.team?.name}
        />
        <DetailItem
          marginBottom={8}
          label="Canal"
          value={atendimento.channel?.name}
        />
        <DetailItem
          layout="row"
          marginBottom={8}
          label="Tags"
          className={classes.tagsWrapper}
          value={
            <TagsV2
              tags={
                atendimento.tags?.map(t => ({
                  id: t.id,
                  cor: t.color,
                  nome: t.name,
                })) || []
              }
              id={atendimento.id}
            />
          }
        />
      </Box>
      <DetailItem
        fullWidth
        marginBottom={8}
        label="Comentários"
        value={
          <Box>
            <InfiniteScroll
              data={comentarios}
              next={onLoadNextComentarios}
              hasMore={Boolean(comentariosCursor.afterCursor)}
              loading={loadingComentarios}
              scrollableTarget="leftColumn"
            >
              {loadingComentarios && comentarios.length === 0 && (
                <CircularProgress size={20} />
              )}

              {!loadingComentarios && comentarios.length === 0 && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height={80}
                >
                  <Typography variant="body2" color="textSecondary">
                    Sem comentários
                  </Typography>
                </Box>
              )}

              {renderComentarios}
            </InfiniteScroll>
          </Box>
        }
      />
    </Box>
  );
}
